/**
 * Human Layer — My Voice fixture.
 *
 * Mirrors `humanLayerProfile.ts`'s discipline exactly: illustrative and
 * minimal, nothing invented that has no source. `status: 'not_configured'`
 * because nobody has saved a sample yet — the same "knowing who somebody is
 * isn't the same as them having set this up" reasoning Profile's seed
 * docblock gives. Tone attributes seed at a neutral midpoint (5/10) rather
 * than a guess at what this person's voice actually is: an invented tone is
 * exactly the kind of fabrication `buildHumanLayerManifest`'s own docblock
 * warns against. In practice a `not_configured` profile's content is never
 * read by a manifest consumer anyway (see `resolveVoice` in
 * `src/domain/humanLayer/manifest.ts`) — these values exist only so the form
 * has somewhere neutral to start, the same role `HUMAN_LAYER_PROFILE_SEED`'s
 * `workingPreferences` play for Profile.
 *
 * `getVoiceProfile` always returns this seed; `updateVoiceProfile`
 * (staticAdapter.ts) builds a fresh object over it rather than writing back,
 * so a page reload restores the seed exactly — same pattern as
 * `humanLayerProfile.ts`.
 */

import type { HumanLayerVoiceProfile } from '../../contracts';
import { DEMO_USER_ID } from './humanLayerProfile';

/** Fixed, because nothing here persists — mirrors `HUMAN_LAYER_PROFILE_VERSION`. */
export const HUMAN_LAYER_VOICE_VERSION = 'fixture-1';

export const HUMAN_LAYER_VOICE_SEED: HumanLayerVoiceProfile = {
  id: 'human-layer-voice-session-user',
  owner: { scope: 'individual', userId: DEMO_USER_ID },
  status: 'not_configured',
  attributes: {
    warmth: 5,
    directness: 5,
    formality: 5,
    humour: 5,
    sentenceLength: 'varied',
    signaturePhrases: [],
  },
  sourceMethod: 'pasted_text',
  samples: [],
  consentRecorded: false,
  // Inert in this slice (no audio is transcribed either way) — fixed to the
  // policy D-058 sets. See the "human layer: voice" section of contracts.ts.
  transcriptionLocality: 'local_only',
  audit: {
    createdBy: DEMO_USER_ID,
    createdAt: '2026-01-05',
    updatedBy: DEMO_USER_ID,
    updatedAt: '2026-01-05',
    version: HUMAN_LAYER_VOICE_VERSION,
  },
};
